import type { Maybe } from '@adornis/base/utilTypes.js';
import { findEntityByText, getByID } from '@adornis/baseql/operations/mongo.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import type { EntityPickerOperation } from '@adornis/entity-picker/entity-picker-operation.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { ListAspect } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { from, map } from 'rxjs';
import { Kostenstelle } from '../../../groups/entities/Kostenstelle.js';
import { School } from '../../../groups/entities/School.js';
import { useSelectableSchoolIDs } from '../../../permissions/client/useSelectableSchoolIDs.js';
import type { ContractSupplementApplyHistoryForPath } from '../../entities/AppliedContract.js';
import { type ContractSupplementChange, ContractSupplementDataType } from '../../entities/ContractSupplement.js';
import type { KostenstelleProzentsatz } from '../../entities/vertrag-sektionen/vertrag-eingruppierung/KostenstelleProzentsatz.js';

const buildHistoryFromKostenstellenObject = (index: number, key: string, vals: ContractSupplementChange[]) => {
  const parsedVals = vals.filter(v => v.type === ContractSupplementDataType.JSON).map(v => JSON.parse(v.value));

  const retrievedValues = parsedVals.map(v => v[index]?.[key] ?? 'X');

  // filter out an X if the value at the previous index was also an x. We only need this as we strip out the history from an object and not from the path. In reality there will never be an entry which deletes a deleted value (and if, so we ignore it ^^)
  const filteredValues: any[] = [];
  let wasX = false;
  for (let i = 0; i < retrievedValues.length; i++) {
    if (i > 0 && wasX && retrievedValues[i] === 'X') {
      continue;
    }
    if (retrievedValues[i] === 'X') {
      wasX = true;
    } else {
      wasX = false;
    }
    filteredValues.push(retrievedValues[i]);
  }

  return filteredValues;
};

const getAsString = (value: Maybe<string[]>): Renderable => {
  const arrow = (index: number) =>
    index < (value ?? []).length - 1 ? html`<span class="text-base-content/70"> → </span>` : nothing;
  return repeat(
    value ?? [],
    (str, index) => html`<span class="${index === 0 ? 'font-semibold' : ''}"> ${str} </span> ${arrow(index)}`,
  ) as Renderable;
};

export const ResolveKostenStellenNames = stateful((props: { kostenstelleIDs: Maybe<string[]> }) => {
  const { value, loading } = useQuery(
    () => getByID(Kostenstelle, props.kostenstelleIDs ?? [])({ _id: 1, name: 1 }),
    [props.kostenstelleIDs],
  );

  if (loading) return '⌛';

  const names: string[] = [];

  for (const id of props.kostenstelleIDs ?? []) {
    names.push(value?.find(v => v._id === id)?.name ?? 'X');
  }

  return getAsString(names);
});

/** searches kostenstellen by name and only keeps the ones belonging to the given school (the school relation is resolved, so we cannot filter on the server) */
const makeKostenstelleOperation = (schoolID: Maybe<string>): EntityPickerOperation<Kostenstelle, [AdornisFilter]> => {
  const operation = (_skip: number, _limit: number, filter: AdornisFilter) => (gqlFields: object) => {
    const search: Promise<Kostenstelle[]> = schoolID
      ? (findEntityByText(Kostenstelle, ['name'], filter.search ?? '', 'i')(gqlFields as never) as unknown as Promise<
          Kostenstelle[]
        >)
      : Promise.resolve([]);

    return from(search).pipe(map(kostenstellen => kostenstellen.filter(k => k.school?._id === schoolID)));
  };
  return operation as unknown as EntityPickerOperation<Kostenstelle, [AdornisFilter]>;
};

interface KostenstelleProzentsatzFieldProps {
  value: Maybe<KostenstelleProzentsatz>;
  disabled?: boolean;
  /** the total weekly hours of the eingruppierung. used to convert between hours and percent */
  fullHours: number;
  /** the hours currently entered for this kostenstelle, owned by the editor while the user types */
  hours: number;
  /** the percentage these hours would be saved as, computed by the editor for the whole set of rows */
  percent: number;
  history?: Maybe<ContractSupplementApplyHistoryForPath>;
  index?: number;
  singleKostenstelle?: boolean;
  /** fired on every keystroke so the sum stays live */
  onHoursChanged: (hours: number) => void;
  /** fired when the user leaves the field, this is what writes the percentage */
  onHoursPicked: (hours: number) => void;
  /** used when the eingruppierung has no hours and the share is entered directly */
  onPercentPicked: (percent: number) => void;
  onChanged: (value: KostenstelleProzentsatz) => void;
  onDelete?: () => void;
}

/**
 * One row for editing a KostenstelleProzentsatz: school, kostenstelle (scoped to the school) and the paid hours.
 * The hours are converted to a percentage internally, as the persisted value is a percentage.
 */
export const KostenstelleProzentsatzField = stateful((props: KostenstelleProzentsatzFieldProps) => {
  // the school is not persisted on the KostenstelleProzentsatz, it is derived from the picked kostenstelle.
  // a manual selection wins over the derived value so the user can switch schools before picking a kostenstelle.
  const [manuallySelectedSchoolID, setManuallySelectedSchoolID] = useState<Maybe<string>>(null);

  // a schulleitung may only book onto their own school, so the picker is narrowed down and - if that leaves
  // exactly one school - preselected so only the kostenstelle is left to pick
  const selectableSchoolIDs = useSelectableSchoolIDs();
  const onlySelectableSchoolID = selectableSchoolIDs?.length === 1 ? selectableSchoolIDs[0] : null;

  const kostenstelleProzentsatz = props.value;

  const selectedSchoolID =
    manuallySelectedSchoolID ?? kostenstelleProzentsatz?.kostenstelle?.school?._id ?? onlySelectableSchoolID ?? null;

  const kostenstelleOperation = useMemo(() => [makeKostenstelleOperation(selectedSchoolID)], [selectedSchoolID]);

  if (!kostenstelleProzentsatz) return nothing;

  let kostenstelleHistory = buildHistoryFromKostenstellenObject(
    props.index ?? 0,
    'kostenstelleID',
    props.history?.changes ?? [],
  );

  if (!kostenstelleHistory.length) {
    kostenstelleHistory = (props.history?.changes?.filter(c => c.path.includes('kostenstelleID')) ?? []).map(
      c => c.value,
    );
  }

  const prozentsatzHistory = buildHistoryFromKostenstellenObject(
    props.index ?? 0,
    'prozentsatz',
    props.history?.changes ?? [],
  );

  const hasFullHours = Number.isFinite(props.fullHours) && props.fullHours > 0;
  // the editor distributes over all rows at once, so this already matches what a save would write
  const derivedPercent = hasFullHours ? props.percent : (kostenstelleProzentsatz.prozentsatz ?? 0);

  const historyBorder = (hasHistory: boolean) => (hasHistory ? 'martinus-history-marker p-0.5' : '');

  return html`
    <div class="grid grid-cols-1 lg:grid-cols-3 items-center gap-2">
      ${EntityPickerInput({
        class: School,
        filter: new AdornisFilter({
          searchFields: ['name'],
          sortingFields: ['name'],
          sortingDirections: ['asc'],
          aspects: selectableSchoolIDs ? [new ListAspect({ fieldName: '_id', values: selectableSchoolIDs })] : [],
        }),
        selectionSet: { _id: 1, name: 1 },
        renderString: (s: School) => s.name ?? '',
        placeholder: 'Schule',
        placeholderMode: 'hide',
        clearable: true,
        disabled: props.disabled,
        value: selectedSchoolID ?? '',
        onEntityPicked: (school: School | null) => {
          setManuallySelectedSchoolID(school?._id ?? null);

          // the kostenstelle belongs to the old school, the entered hours stay so the user only has to
          // pick the new kostenstelle
          kostenstelleProzentsatz.kostenstelleID = null;
          kostenstelleProzentsatz.kostenstelle = null;
          props.onChanged(kostenstelleProzentsatz);
        },
      })}

      <div class="tooltip ${historyBorder(kostenstelleHistory.length > 1)}" ${hoverTooltip()}>
        ${
          kostenstelleHistory.length > 1
            ? html`
                <div class="tooltip-content">
                  <div class="flex flex-row items-center gap-1">
                    ${ResolveKostenStellenNames({ kostenstelleIDs: kostenstelleHistory })}
                  </div>
                </div>
              `
            : nothing
        }
        ${EntityPickerInput({
          class: Kostenstelle,
          operation: kostenstelleOperation,
          filter: new AdornisFilter({ searchFields: ['name'] }),
          selectionSet: { _id: 1, _class: 1, name: 1, school: { _id: 1, name: 1 } },
          renderString: (k: Maybe<Kostenstelle>) => k?.name ?? '',
          placeholder: 'Kostenstelle',
          placeholderMode: 'hide',
          clearable: true,
          disabled: props.disabled,
          value: kostenstelleProzentsatz.kostenstelleID ?? '',
          onEntityPicked: (kostenstelle: Kostenstelle | null) => {
            if (!kostenstelle) return;
            kostenstelleProzentsatz.kostenstelleID = kostenstelle._id;
            kostenstelleProzentsatz.kostenstelle = kostenstelle;
            props.onChanged(kostenstelleProzentsatz);
          },
        })}
      </div>

      <div class="flex flex-row items-center gap-2">
        <div class="tooltip flex-1 ${historyBorder(prozentsatzHistory.length > 1)}" ${hoverTooltip()}>
          ${
            prozentsatzHistory.length > 1
              ? html`
                  <div class="tooltip-content">
                    <div class="flex flex-col gap-2 text-left">
                      <p>
                        Das System speichert im Hintergrund Prozentwerte, welche kaufmännisch auf zwei Nachkommastellen
                        gerundet werden. Alle Stunden von Eingruppierung 1 und 2 müssen über alle Kostenstellen vergeben
                        werden.
                      </p>
                      <div class="flex flex-row items-center gap-1">${getAsString(prozentsatzHistory)}</div>
                    </div>
                  </div>
                `
              : nothing
          }
          ${
            hasFullHours
              ? NumberInput({
                  value: props.hours,
                  min: 0,
                  step: 0.01,
                  disabled: props.disabled,
                  suffix: props.singleKostenstelle
                    ? html`<span class="whitespace-nowrap text-sm text-base-content/70"> Std. </span>`
                    : html`<span class="whitespace-nowrap text-sm text-base-content/70">
                        Std. (${derivedPercent.toFixed(2).replace('.', ',')} %)
                      </span>`,
                  onChanged: (newValue: number | null) => props.onHoursChanged(newValue ?? 0),
                  onPicked: (newValue: number | null) => props.onHoursPicked(newValue ?? 0),
                })
              : NumberInput({
                  placeholder: 'Anteil',
                  value: kostenstelleProzentsatz.prozentsatz ?? 0,
                  min: 0,
                  max: 100,
                  disabled: props.disabled,
                  suffix: html`<span class="text-sm text-base-content/70">%</span>`,
                  onPicked: (newValue: number | null) => props.onPercentPicked(newValue ?? 0),
                })
          }
        </div>
        <button
          class="btn btn-ghost btn-sm btn-square"
          aria-label="Kostenstelle entfernen"
          ?disabled=${props.disabled}
          @click=${() => props.onDelete?.()}
        >
          <span class="icon-[material-symbols--delete-outline] text-error text-lg"></span>
        </button>
      </div>
    </div>
  `;
});
